import { useState } from 'react'
import type { AssetCheck } from '@/types/assetCheck'

interface ResultDetailAccordionProps {
  check: AssetCheck
}

function pct(v: string | null): string {
  if (v === null) return '—'
  return `${(Number(v) * 100).toFixed(1)} %`
}

export function ResultDetailAccordion({ check }: ResultDetailAccordionProps) {
  const [openSection, setOpenSection] = useState<string | null>(null)
  const toggle = (s: string) => setOpenSection(openSection === s ? null : s)

  const { metrics, portfolio_impact, compliance, tax_forecast } = check

  return (
    <div className="space-y-1 text-sm" data-testid="result-detail-accordion">
      <Section
        label="Performance & Returns"
        open={openSection === 'perf'}
        onClick={() => toggle('perf')}
      >
        <Row label="YTD" value={pct(metrics.performance.ytd)} />
        <Row label="1 Jahr" value={pct(metrics.performance['1y'])} />
        <Row label="3 Jahre" value={pct(metrics.performance['3y'])} />
        <Row label="5 Jahre" value={pct(metrics.performance['5y'])} />
      </Section>
      <Section
        label="Volatilität & Drawdown"
        open={openSection === 'vola'}
        onClick={() => toggle('vola')}
      >
        <Row label="Vola 30d (annual.)" value={pct(metrics.volatility['30d'])} />
        <Row label="Vola 90d (annual.)" value={pct(metrics.volatility['90d'])} />
        <Row label="Vola 1y (annual.)" value={pct(metrics.volatility['1y'])} />
        <Row label="Max-Drawdown 5y" value={pct(metrics.drawdown.max_5y)} />
        <Row label="Schlimmster 90d in 5y" value={pct(metrics.drawdown.worst_90d_5y)} />
      </Section>
      <Section
        label={`Korrelations-Matrix (${metrics.correlations.length})`}
        open={openSection === 'corr'}
        onClick={() => toggle('corr')}
      >
        {metrics.correlations.length === 0 ? (
          <div className="text-slate-500">Kein Portfolio-Bestand</div>
        ) : (
          metrics.correlations.map((c) => (
            <Row key={c.isin} label={c.ticker ?? c.isin} value={Number(c.value).toFixed(2)} />
          ))
        )}
      </Section>
      <Section label="Portfolio-Impact" open={openSection === 'pi'} onClick={() => toggle('pi')}>
        <div className="text-slate-500 mb-1">
          Cluster:{' '}
          <span className="font-mono">{portfolio_impact.cluster_assignment.name ?? '—'}</span>{' '}
          (Source: {portfolio_impact.cluster_assignment.source})
        </div>
        {portfolio_impact.cluster_impact.map((c) => (
          <Row
            key={c.cluster_name}
            label={c.label}
            value={`${pct(c.before_pct)} → ${pct(c.after_pct)}${c.max_pct ? ` (max ${pct(c.max_pct)})` : ''}`}
          />
        ))}
        <Row
          label="Neue Position-Gewichtung"
          value={pct(portfolio_impact.position_after.weight_pct)}
        />
      </Section>
      <Section
        label="Compliance + Steuer-Forecast"
        open={openSection === 'comp'}
        onClick={() => toggle('comp')}
      >
        <Row
          label="Hard-Rule-Verletzungen"
          value={
            compliance.hard_rule_violations.length === 0
              ? 'keine'
              : compliance.hard_rule_violations.map((v) => v.rule_name).join(', ')
          }
        />
        <Row label="Neukauf" value={tax_forecast.is_new_position ? 'ja' : 'nein'} />
        <Row label="Steuerfrei ab" value={tax_forecast.tax_free_date ?? '—'} />
      </Section>
    </div>
  )
}

function Section(props: {
  label: string
  open: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <div className="rounded border border-slate-200 bg-white">
      <button
        type="button"
        onClick={props.onClick}
        className="w-full flex items-center justify-between px-3 py-2 text-left font-medium text-slate-800 hover:bg-slate-50"
        data-testid={`section-${props.label}`}
      >
        <span>{props.label}</span>
        <span aria-hidden>{props.open ? '▾' : '▸'}</span>
      </button>
      {props.open && (
        <div className="px-3 py-2 border-t border-slate-200 space-y-1">{props.children}</div>
      )}
    </div>
  )
}

function Row(props: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-600">{props.label}</span>
      <span className="font-mono text-slate-900">{props.value}</span>
    </div>
  )
}
